import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Loader2 } from "lucide-react";

type Lang = "en" | "fr";
type SourcePillar = "coaching" | "fractional" | "mentoring" | "microservices" | "general";

const COPY: Record<Lang, Record<string, string>> = {
  en: {
    title: "Book a discovery call",
    desc: "Tell us a bit about your business. We'll match you with the right operator and reply within one business day.",
    name: "Full name",
    email: "Email",
    phone: "Phone (optional)",
    company: "Company (optional)",
    stage: "Stage (optional)",
    stagePlaceholder: "e.g. pre-revenue, scaling, post-Series A",
    challenge: "What's the main challenge? (optional)",
    preferred: "Preferred date & time (optional)",
    submit: "Request discovery call",
    submitting: "Sending…",
    successTitle: "Request received",
    successBody: "We'll be in touch within one business day to confirm a time.",
    close: "Close",
    error: "Something went wrong. Please try again.",
    required: "Required",
    invalidEmail: "Please enter a valid email",
  },
  fr: {
    title: "Réserver un appel découverte",
    desc: "Parlez-nous brièvement de votre activité. Nous vous mettrons en relation avec le bon opérateur et reviendrons vers vous sous un jour ouvré.",
    name: "Nom complet",
    email: "E-mail",
    phone: "Téléphone (optionnel)",
    company: "Entreprise (optionnel)",
    stage: "Stade (optionnel)",
    stagePlaceholder: "ex. pré-revenu, scaling, post-Série A",
    challenge: "Quel est le défi principal ? (optionnel)",
    preferred: "Date & heure souhaitées (optionnel)",
    submit: "Demander un appel découverte",
    submitting: "Envoi…",
    successTitle: "Demande reçue",
    successBody: "Nous reviendrons vers vous sous un jour ouvré pour confirmer un créneau.",
    close: "Fermer",
    error: "Une erreur est survenue. Veuillez réessayer.",
    required: "Requis",
    invalidEmail: "Veuillez saisir un e-mail valide",
  },
};

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  sourcePillar?: SourcePillar;
  lang?: Lang;
}

export function DiscoveryCallModal({ open, onOpenChange, sourcePillar = "coaching", lang: langProp }: Props) {
  const { i18n } = useTranslation();
  const raw = (langProp ?? (i18n.language?.slice(0, 2) ?? "en")) as string;
  const lang: Lang = raw === "fr" ? "fr" : "en"; // EN fallback for ES/PT
  const t = COPY[lang];

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [stage, setStage] = useState("");
  const [challenge, setChallenge] = useState("");
  const [preferredDt, setPreferredDt] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setName(""); setEmail(""); setPhone(""); setCompany(""); setStage(""); setChallenge(""); setPreferredDt("");
    setSuccess(false); setError(null); setSubmitting(false);
  }

  function handleOpenChange(v: boolean) {
    if (!v) reset();
    onOpenChange(v);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) { setError(t.required + ": " + t.name); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError(t.invalidEmail); return; }

    setSubmitting(true);
    try {
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/book-discovery-call`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          source_pillar: sourcePillar,
          customer_name: name.trim(),
          customer_email: email.trim(),
          customer_phone: phone.trim() || null,
          company: company.trim() || null,
          stage: stage.trim() || null,
          challenge: challenge.trim().slice(0, 500) || null,
          preferred_datetime: preferredDt ? new Date(preferredDt).toISOString() : null,
          preferred_lang: raw,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      setSuccess(true);
    } catch (err) {
      console.error("Discovery call submit failed", err);
      setError(t.error);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        {success ? (
          <div className="py-6 text-center space-y-4">
            <CheckCircle2 className="mx-auto h-12 w-12 text-primary" />
            <DialogHeader>
              <DialogTitle className="text-center">{t.successTitle}</DialogTitle>
              <DialogDescription className="text-center">{t.successBody}</DialogDescription>
            </DialogHeader>
            <Button onClick={() => handleOpenChange(false)} className="mt-4">{t.close}</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{t.title}</DialogTitle>
              <DialogDescription>{t.desc}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="dc-name">{t.name} *</Label>
                <Input id="dc-name" value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dc-email">{t.email} *</Label>
                <Input id="dc-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required maxLength={200} />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="dc-phone">{t.phone}</Label>
                  <Input id="dc-phone" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dc-company">{t.company}</Label>
                  <Input id="dc-company" value={company} onChange={(e) => setCompany(e.target.value)} maxLength={120} />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="dc-stage">{t.stage}</Label>
                <Input id="dc-stage" value={stage} onChange={(e) => setStage(e.target.value)} maxLength={120} placeholder={t.stagePlaceholder} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dc-preferred">{t.preferred}</Label>
                <Input id="dc-preferred" type="datetime-local" value={preferredDt} onChange={(e) => setPreferredDt(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dc-challenge">{t.challenge}</Label>
                <Textarea id="dc-challenge" value={challenge} onChange={(e) => setChallenge(e.target.value)} maxLength={500} rows={3} />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" disabled={submitting} className="w-full">
                {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t.submitting}</> : t.submit}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default DiscoveryCallModal;
